'use client'

import { OpenQuestion, QuestionType } from '@/api/services/types/questionTypes'
import { useTestResponse } from '@/hooks/useTestResponse'
import * as React from 'react'
import StartCard from '../components/StartCard'
import { OpenQuestionCard } from './OpenQuestionCard'

interface MockQuestion {
  id: number
  title: string
  placeholder?: string
  maxLength?: number
  timeLimit: number
}

const mockQuestions: MockQuestion[] = [
  {
    id: 1,
    title: 'Jak się dziś czujesz?',
    maxLength: 200,
    timeLimit: 10
  },
  {
    id: 2,
    title: 'Co było dla Ciebie największym wyzwaniem w tym tygodniu?',
    maxLength: 300,
    timeLimit: 15
  },
  {
    id: 3,
    title: 'Jakie masz plany na nadchodzący weekend?',
    maxLength: 250,
    timeLimit: 10
  }
]

interface OpenQuestionTaskProps {
  onComplete: () => void
  onStart: () => void
  data?: OpenQuestion[]
  moduleId: string
  questionId: string
}

function isOpenQuestion(question: OpenQuestion | MockQuestion): question is OpenQuestion {
  return 'questionText' in question
}

export function OpenQuestionTask({
  onComplete,
  onStart,
  data,
  moduleId,
  questionId
}: OpenQuestionTaskProps) {
  const [currentIndex, setCurrentIndex] = React.useState(0)
  const [answers, setAnswers] = React.useState<string[]>(
    Array(data?.length || mockQuestions.length).fill('')
  )
  const [timeLeft, setTimeLeft] = React.useState(
    data?.[0]?.timeLimitSeconds || mockQuestions[0].timeLimit
  )
  const [disabled, setDisabled] = React.useState(false)
  const [showStart, setShowStart] = React.useState(false)
  const { addQuestionResponse } = useTestResponse()
  const startTimeRef = React.useRef<number>(Date.now())

  const currentQuestion = data?.[currentIndex] || mockQuestions[currentIndex]

  React.useEffect(() => {
    setTimeLeft(
      data?.[currentIndex]?.timeLimitSeconds || mockQuestions[currentIndex].timeLimit
    )
    setDisabled(false)

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          setDisabled(true)
          setTimeout(() => handleNext(), 1000)
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [currentIndex, data])

  const handleAnswerChange = (value: string) => {
    setAnswers((prev) => {
      const updated = [...prev]
      updated[currentIndex] = value
      return updated
    })
  }

  const handleNext = async () => {
    if (currentIndex < (data?.length || mockQuestions.length) - 1) {
      await addQuestionResponse(moduleId, {
        questionId: questionId,
        questionType: QuestionType.OPEN_QUESTION,
        openQuestionAnswer: {
          textAnswer: answers[currentIndex],
          responseTimeMillis: Date.now() - startTimeRef.current
        }
      })

      setCurrentIndex((i) => i + 1)
      startTimeRef.current = Date.now()
    } else {
      await addQuestionResponse(moduleId, {
        questionId: questionId,
        questionType: QuestionType.OPEN_QUESTION,
        openQuestionAnswer: {
          textAnswer: answers[currentIndex],
          responseTimeMillis: Date.now() - startTimeRef.current
        }
      })

      setDisabled(true)
      onComplete()
    }
  }

  if (showStart) {
    return (
      <StartCard
        description={
          'W tym zadaniu będziesz musiał samodzielnie wpisać odpowiedzi na pytania. Masz określony czas na odpowiedź na każde pytanie i ograniczoną ilość słów.'
        }
        onStart={() => {
          setShowStart(false)
          onStart()
        }}
        title={'Pytanie Otwarte'}
      />
    )
  }

  return (
    <div className="mx-auto flex min-h-[40vh] w-full max-w-xl flex-col space-y-2 p-4 text-center">
      <div className="text-muted-foreground pr-2 text-right text-sm">
        Czas pozostały: {timeLeft}s
      </div>
      <OpenQuestionCard
        className={disabled ? 'pointer-events-none opacity-50' : ''}
        maxLength={
          isOpenQuestion(currentQuestion)
            ? currentQuestion.textFieldLength
            : currentQuestion.maxLength
        }
        onChange={handleAnswerChange}
        onNext={handleNext}
        placeholder={
          isOpenQuestion(currentQuestion) ? undefined : currentQuestion.placeholder
        }
        title={
          isOpenQuestion(currentQuestion)
            ? currentQuestion.questionText
            : currentQuestion.title
        }
        value={answers[currentIndex]}
      />
    </div>
  )
}
